// Type definitions
import { PricingConfiguration, UserSelection } from '@la/types';

// Type definitions
interface SingleOptionPriceRule {
  inventory?: number;
  option: string;
  priceChange: number;
  type: 'single';
  value: string;
}

interface CombinationPriceRule {
  conditions: Array<{
    option: string;
    value: string;
  }>;
  inventory?: number;
  priceChange: number;
  type: 'combination';
}

type PriceRule = CombinationPriceRule | SingleOptionPriceRule;

// Convert raw price data to price rules
function convertToPriceRules(rawData: PricingConfiguration[]): PriceRule[] {
  return rawData
    .filter((item) => item.price !== undefined) // Only process items with price
    .map((item) => {
      // Split by comma to get individual property mappings
      const mappings = item.propertiesMapping.split(',').map((m) => m.trim());

      if (mappings.length === 1) {
        // Single option rule
        const [option, value] = mappings[0].split(':');
        return {
          option: option.trim(),
          priceChange: item.price!,
          type: 'single' as const,
          value: value.trim(),
          ...(item.inventory !== undefined && { inventory: item.inventory }),
        };
      } else {
        // Combination rule
        const conditions = mappings.map((mapping) => {
          const [option, value] = mapping.split(':');
          return {
            option: option.trim(),
            value: value.trim(),
          };
        });
        return {
          type: 'combination' as const,
          conditions,
          priceChange: item.price!,
          ...(item.inventory !== undefined && { inventory: item.inventory }),
        };
      }
    });
}

// Helper function to check if a single rule is included in a combination rule
function isSingleRuleIncludedInCombination(
  singleRule: SingleOptionPriceRule,
  combinationRule: CombinationPriceRule
): boolean {
  return combinationRule.conditions.some(
    (condition) =>
      condition.option === singleRule.option &&
      condition.value === singleRule.value
  );
}

// Helper function to check if a combination is a subset of another combination
function isCombinationSubsetOfAnother(
  subset: CombinationPriceRule,
  superset: CombinationPriceRule
): boolean {
  if (subset.conditions.length >= superset.conditions.length) {
    return false;
  }

  return subset.conditions.every((subCondition) =>
    superset.conditions.some(
      (superCondition) =>
        superCondition.option === subCondition.option &&
        superCondition.value === subCondition.value
    )
  );
}

// Pricing calculation function with rule precedence
export function calculatePrice(
  basePrice: number,
  selectedOptions: UserSelection[],
  pricingConfigurations: PricingConfiguration[]
): number {
  let totalPrice = basePrice;

  // convert UserSelections into SelecteOptions
  // convert PricingConfigurations into PriceRules
  const priceRules = convertToPriceRules(pricingConfigurations);

  // Find all matching rules
  const matchingRules: PriceRule[] = [];

  for (const rule of priceRules) {
    if (rule.type === 'single') {
      const isMatch = selectedOptions.some(
        (selection) =>
          selection.optionId === rule.option &&
          selection.selectedValue === rule.value
      );
      if (isMatch) {
        matchingRules.push(rule);
      }
    } else if (rule.type === 'combination') {
      const allConditionsMet = rule.conditions.every((condition) =>
        selectedOptions.some(
          (selection) =>
            selection.optionId === condition.option &&
            selection.selectedValue === condition.value
        )
      );
      if (allConditionsMet) {
        matchingRules.push(rule);
      }
    }
  }

  // Filter out rules that are subsets of other matching rules
  const applicableRules = matchingRules.filter((rule) => {
    // Check if this rule is superseded by a more specific combination
    return !matchingRules.some((otherRule) => {
      if (rule === otherRule) {
        return false;
      }

      if (otherRule.type === 'combination') {
        // If current rule is single, check if it's included in the combination
        if (rule.type === 'single') {
          return isSingleRuleIncludedInCombination(rule, otherRule);
        }
        // If current rule is combination, check if it's a subset
        if (rule.type === 'combination') {
          return isCombinationSubsetOfAnother(rule, otherRule);
        }
      }

      return false;
    });
  });

  // Apply all applicable rules
  for (const rule of applicableRules) {
    totalPrice += rule.priceChange;
  }

  return totalPrice;
}
